import { buttonClasses } from "@scaffquote/ui";
import { CircleAlert } from "lucide-react";
import { isRouteErrorResponse, Link, useRouteError } from "react-router";
import { ApiError } from "../lib/api.ts";

function describe(error: unknown): { title: string; detail: string } {
  if (isRouteErrorResponse(error)) {
    return error.status === 404
      ? {
          title: "Page not found",
          detail: "That page doesn't exist, or it belongs to another organisation.",
        }
      : { title: `Something went wrong (${error.status})`, detail: error.statusText };
  }
  if (error instanceof ApiError) {
    if (error.status === 404) {
      return {
        title: "Not found",
        detail: "It may have been deleted, or it belongs to another organisation.",
      };
    }
    if (error.code === "NETWORK") return { title: "You're offline", detail: error.message };
    return { title: "Something went wrong", detail: error.message };
  }
  return {
    title: "Something went wrong",
    detail:
      error instanceof Error ? error.message : "An unexpected error stopped this page loading.",
  };
}

/** Error boundary for routes: says what happened and offers a way on. */
export function RouteError() {
  const error = useRouteError();
  const { title, detail } = describe(error);
  // The API's own code (NOT_FOUND, TENANT_NOT_FOUND …), or HTTP_<status> when the
  // reply wasn't scaffquote's envelope at all – which tells a real missing record
  // apart from another server answering, at a glance.
  const code = error instanceof ApiError ? error.code : null;
  return (
    <div
      role="alert"
      className="mx-auto my-10 max-w-lg rounded-xl border border-line bg-surface p-6 text-center"
    >
      <CircleAlert aria-hidden className="mx-auto mb-2 size-8 text-danger" />
      <h1 className="font-bold text-xl">{title}</h1>
      <p className="mt-2 text-ink-muted">{detail}</p>
      {code ? <p className="mt-2 font-mono text-ink-muted text-xs">Error code: {code}</p> : null}
      <div className="mt-5 flex flex-wrap justify-center gap-2">
        <button
          type="button"
          className={buttonClasses({ variant: "secondary" })}
          onClick={() => window.location.reload()}
        >
          Try again
        </button>
        <Link to="/" className={buttonClasses()}>
          Go to Today
        </Link>
      </div>
    </div>
  );
}
